<template>
  <div>
    <GatewayConfig v-if="shouldShowGatewayConfig" />
    <layout v-else toolbar_off="1" :title="$route.name" :rotas="rotas">
    <v-container fluid class="pa-0">
      <v-card class="whatsapp-workspace" :class="{ 'whatsapp-workspace-dark': isDark }" :dark="isDark">
        <aside class="conversation-sidebar">
          <div class="sidebar-header">
            <div><h2>Conversas</h2><small style="color: #888 !important;">Central de atendimento</small></div>
            <v-chip small :color="gatewayStatusChipColor" dark>{{ gatewayStatusLabel }}</v-chip>
            <v-btn icon title="Desconectar gateway" @click="disconnectGateway"><v-icon>mdi-power</v-icon></v-btn>
            <v-btn icon :color="hasFilters ? '#1f8f55' : undefined" @click="filterMenu = !filterMenu"><v-icon>mdi-filter-variant</v-icon></v-btn>
            <v-btn icon :loading="loading" @click="loadConversations"><v-icon>mdi-refresh</v-icon></v-btn>
          </div>
          <v-tabs v-model="sidebarTab" class="conversation-tabs" grow height="38" mandatory>
            <v-tab @click="setSidebarTab('whatsapp')">Clientes</v-tab>
            <v-tab @click="setSidebarTab('internal')">Internos</v-tab>
          </v-tabs>
          <v-card v-if="filterMenu" class="filter-popover" :dark="isDark">
            <div class="filter-popover-header">
              <strong>Filtros</strong>
              <v-btn icon small @click="filterMenu = false"><v-icon small>mdi-close</v-icon></v-btn>
            </div>
            <v-text-field v-model="dateFrom" type="date" label="De" dense outlined hide-details />
            <v-text-field v-model="dateTo" type="date" label="Até" dense outlined hide-details />
            <div class="filter-actions">
              <v-btn text small @click="clearFilters">Limpar</v-btn>
              <v-spacer />
              <v-btn small color="#1f8f55" dark @click="applyFilters">Aplicar</v-btn>
            </div>
          </v-card>
          <v-text-field v-if="isWhatsappTab" v-model="search" class="conversation-search" prepend-inner-icon="mdi-magnify" placeholder="Buscar conversa" solo dense flat hide-details />
          <v-text-field v-else v-model="internalSearch" class="conversation-search" prepend-inner-icon="mdi-magnify" placeholder="Buscar funcionário" solo dense flat hide-details />
          <div v-if="isWhatsappTab" class="conversation-list">
            <button v-for="conversation in filteredConversations" :key="conversation._id" class="conversation-item" :class="{ active: selectedConversation && selectedConversation._id === conversation._id }" @click="openConversation(conversation)">
              <v-avatar color="#e8f5e9" size="42">
                <img :src="conversationAvatar(conversation)" alt="">
              </v-avatar>
              <div class="conversation-copy">
                <strong>{{ conversationName(conversation) }}</strong>
                <small>{{ conversation.last_message_text || "Sem mensagem" }}</small>
              </div>
              <v-chip v-if="conversation.unread_count" color="#25D366" dark x-small>{{ conversation.unread_count }}</v-chip>
            </button>
            <div v-if="!loading && !filteredConversations.length" class="empty-state">Nenhuma conversa encontrada.</div>
          </div>
          <div v-else class="conversation-list">
            <button v-for="employee in filteredEmployees" :key="employee._id" class="conversation-item internal-user-item" :class="{ active: selectedInternalUser && selectedInternalUser._id === employee._id }" @click="openInternalChat(employee)">
              <v-avatar color="#182229" size="42">
                <img v-if="employeeAvatar(employee)" :src="employeeAvatar(employee)" alt="">
                <v-icon v-else color="#9be7bd">mdi-account</v-icon>
              </v-avatar>
              <div class="conversation-copy">
                <strong>{{ employee.nome }}</strong>
                <small>{{ employeeStatusLabel(employee) }}</small>
              </div>
              <v-chip v-if="internalUnread(employee)" color="#25D366" dark x-small>{{ internalUnread(employee) }}</v-chip>
              <span class="presence-dot" :class="`presence-${employee.realtime_status || 'offline'}`" />
            </button>
            <div v-if="!employees.length" class="empty-state">Nenhum funcionário encontrado.</div>
          </div>
        </aside>

        <main class="chat-panel">
          <template v-if="selectedInternalUser">
            <header class="chat-header">
              <v-avatar class="mr-3" color="#182229" size="42">
                <img v-if="employeeAvatar(selectedInternalUser)" :src="employeeAvatar(selectedInternalUser)" alt="">
                <v-icon v-else color="#9be7bd">mdi-account</v-icon>
              </v-avatar>
              <div>
                <h3>{{ selectedInternalUser.nome }}</h3>
                <small>{{ employeeStatusLabel(selectedInternalUser) }} · conversa interna</small>
              </div>
              <v-spacer />
              <span class="presence-dot" :class="`presence-${selectedInternalUser.realtime_status || 'offline'}`" />
            </header>
            <v-alert v-if="errorMessage" class="ma-3 mb-0" type="error" dense dismissible @input="errorMessage = ''">{{ errorMessage }}</v-alert>
            <section ref="internalMessageStream" class="message-stream">
              <div v-for="message in internalMessages" :key="message._id" class="message-row" :class="{ outgoing: isInternalMessageMine(message) }">
                <div class="message-bubble">
                  <div>{{ message.text }}</div>
                  <small>{{ formatDate(message.created_at) }}</small>
                </div>
              </div>
            </section>
            <footer class="composer">
              <v-text-field v-model="newInternalMessage" placeholder="Mensagem interna" solo dense flat hide-details @keyup.enter="sendInternalMessage" />
              <v-btn icon color="#1f8f55" :loading="sendingInternal" :disabled="!newInternalMessage.trim()" @click="sendInternalMessage"><v-icon>mdi-send</v-icon></v-btn>
            </footer>
          </template>
          <template v-else-if="selectedConversation">
            <header class="chat-header">
              <v-avatar class="mr-3" color="#e8f5e9" size="42">
                <img :src="conversationAvatar(selectedConversation)" alt="">
              </v-avatar>
              <div>
                <h3>{{ (selectedConversation.client && selectedConversation.client.nome) || selectedConversation.contact_name }}</h3>
                <small>{{ conversationPhoneLabel }}</small>
              </div>
              <v-spacer />
              <v-chip v-if="selectedConversation.assigned_user" small color="#e8f5e9" text-color="#187a48">
                <v-icon small left>mdi-headset</v-icon>{{ selectedConversation.assigned_user.nome }}
              </v-chip>
              <v-btn v-else small color="#25D366" dark :loading="claiming" @click="claimConversation">Assumir atendimento</v-btn>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon :loading="archiving" v-bind="attrs" v-on="on" @click="archiveConversation">
                    <v-icon>mdi-archive-outline</v-icon>
                  </v-btn>
                </template>
                <span>Arquivar conversa</span>
              </v-tooltip>
            </header>
            <v-alert v-if="errorMessage" class="ma-3 mb-0" type="error" dense dismissible @input="errorMessage = ''">{{ errorMessage }}</v-alert>
            <section ref="messageStream" class="message-stream">
              <div v-for="message in messages" :key="message._id" class="message-row" :class="{ outgoing: message.from_me }">
                <div class="message-bubble">
                  <img v-if="message.message_type === 'image' && message.attachment && message.attachment.previewDataUrl" class="message-media" :src="message.attachment.previewDataUrl" alt="Imagem da conversa">
                  <video v-else-if="message.message_type === 'video' && message.attachment && message.attachment.previewDataUrl" class="message-media" :src="message.attachment.previewDataUrl" controls />
                  <div v-else-if="message.message_type === 'audio'">
                    <audio v-if="message.attachment && message.attachment.previewDataUrl" class="message-audio" :src="message.attachment.previewDataUrl" controls />
                    <span v-else><v-icon small>mdi-microphone</v-icon> Áudio</span>
                  </div>
                  <div v-else-if="message.message_type === 'document'"><v-icon small>mdi-file-document-outline</v-icon> {{ message.attachment && message.attachment.name }}</div>
                  <div v-else-if="message.message_type === 'poll'" class="poll-message">
                    <strong><v-icon small>mdi-poll</v-icon> {{ message.poll && message.poll.name }}</strong>
                    <div v-for="option in pollOptions(message)" :key="option.name" class="poll-option">
                      <span>{{ option.name }}</span><small>{{ option.count }} voto(s)</small>
                    </div>
                  </div>
                  <div v-else>{{ message.text }}</div>
                  <small>{{ formatDate(message.timestamp) }} <v-icon v-if="message.from_me" x-small :color="message.delivery_status === 'read' ? '#53bdeb' : '#708078'">{{ statusIcon(message.delivery_status) }}</v-icon></small>
                  <v-btn class="delete-message" icon x-small @click="deleteMessage(message)"><v-icon x-small>mdi-delete-outline</v-icon></v-btn>
                </div>
              </div>
            </section>
            <footer class="composer">
              <input ref="documentInput" type="file" class="d-none" accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt" @change="sendDocument">
              <v-btn icon color="#607d75" :disabled="isAssignedToAnotherUser" @click="$refs.documentInput.click()"><v-icon>mdi-paperclip</v-icon></v-btn>
              <v-btn icon color="#607d75" :disabled="isAssignedToAnotherUser" @click="sendPoll"><v-icon>mdi-poll</v-icon></v-btn>
              <v-text-field v-model="newMessage" :disabled="isAssignedToAnotherUser" :placeholder="composerPlaceholder" solo dense flat hide-details @keyup.enter="sendMessage" />
              <v-btn icon :color="recording ? 'red' : '#607d75'" :disabled="isAssignedToAnotherUser" @click="toggleAudioRecording"><v-icon>{{ recording ? 'mdi-stop-circle' : 'mdi-microphone' }}</v-icon></v-btn>
              <v-btn icon color="#1f8f55" :loading="sending" :disabled="!newMessage.trim() || isAssignedToAnotherUser" @click="sendMessage"><v-icon>mdi-send</v-icon></v-btn>
            </footer>
          </template>
          <div v-else class="empty-chat"><v-icon size="72" color="#b8c5bf">mdi-whatsapp</v-icon><h3>Selecione uma conversa</h3></div>
        </main>

        <aside v-if="selectedConversation" class="client-panel">
          <transition :name="clientPanelView === 'menu' ? 'panel-slide-back' : 'panel-slide'" mode="out-in">
            <div v-if="clientPanelView === 'menu'" key="client-menu" class="client-panel-screen">
              <h3>Cliente</h3>
              <div v-if="linkedClients.length" class="linked-clients-list">
                <div v-for="client in linkedClients" :key="client._id" class="linked-client">
                  <strong>{{ client.nome }}</strong>
                  <small>Principal: {{ client.telefone || "sem telefone" }}</small>
                  <small>WhatsApp: {{ client.whatsapp_phone || conversationPhoneLabel }}</small>
                </div>
              </div>
              <template v-else>
                <p class="caption">Vincule o numero desta conversa a um cadastro existente.</p>
                <v-autocomplete v-model="selectedClientId" :items="clients" item-value="_id" :item-text="clientSearchText" label="Buscar por nome, CPF ou CNPJ" outlined dense />
                <v-btn block color="#1f8f55" dark :disabled="!selectedClientId" @click="confirmLinkClient">Vincular cliente</v-btn>
              </template>
              <v-btn block outlined color="#1f8f55" class="mt-3" @click="openCreateClientFromWhatsapp">
                <v-icon left small>mdi-account-plus-outline</v-icon>
                Criar novo cliente com este contato
              </v-btn>
              <v-divider class="my-5" />
              <div class="client-action-list">
                <button v-for="option in clientPanelOptions" :key="option.view" class="client-action-item" @click="openClientPanelView(option.view)">
                  <span class="client-action-icon"><v-icon>{{ option.icon }}</v-icon></span>
                  <span>
                    <strong>{{ option.title }}</strong>
                    <small>{{ option.description }}</small>
                  </span>
                  <v-icon>mdi-chevron-right</v-icon>
                </button>
              </div>
            </div>
            <div v-else key="client-detail" class="client-panel-screen">
              <div class="client-panel-nav">
                <v-btn icon small @click="backClientPanel"><v-icon>mdi-arrow-left</v-icon></v-btn>
                <strong>{{ clientPanelTitle }}</strong>
              </div>
              <section v-if="clientPanelView === 'attendance'" class="client-detail-section">
                <div v-if="isAssignedToAnotherUser" class="assume-card">
                  <v-icon color="#1f8f55">mdi-account-switch</v-icon>
                  <strong>Quer assumir esse atendimento?</strong>
                  <small>Ele esta com {{ selectedConversation.assigned_user.nome }}. Ao assumir, voce passa a responder o cliente.</small>
                  <v-btn color="#1f8f55" dark :loading="claiming" @click="claimConversation">SIM, assumir</v-btn>
                </div>
                <div v-else class="assume-card">
                  <v-icon color="#1f8f55">mdi-headset</v-icon>
                  <strong>{{ selectedConversation.assigned_user ? 'Atendimento assumido' : 'Atendimento livre' }}</strong>
                  <small>{{ selectedConversation.assigned_user ? `Responsavel: ${selectedConversation.assigned_user.nome}` : 'Ninguem assumiu ainda. Envie uma mensagem ou assuma manualmente.' }}</small>
                  <v-btn v-if="!selectedConversation.assigned_user" color="#1f8f55" dark :loading="claiming" @click="claimConversation">Assumir agora</v-btn>
                </div>
                <div class="timeline-list">
                  <div v-for="item in attendanceHistoryItems" :key="item.id" class="timeline-item">
                    <span class="timeline-dot" />
                    <div>
                      <strong>{{ item.title }}</strong>
                      <small>{{ item.description }}</small>
                      <small>{{ formatDate(item.date) }}</small>
                    </div>
                  </div>
                </div>
              </section>
              <section v-if="clientPanelView === 'linkClient'" class="client-detail-section">
                <div class="assume-card">
                  <v-icon color="#1f8f55">mdi-link-variant</v-icon>
                  <strong>Vincular conversa a clientes</strong>
                  <small>O numero {{ conversationPhoneLabel }} pode ficar associado a mais de um cliente interno, sem alterar o telefone principal do cadastro.</small>
                </div>
                <div v-if="linkedClients.length" class="linked-clients-list compact">
                  <div v-for="client in linkedClients" :key="client._id" class="linked-client">
                    <strong>{{ client.nome }}</strong>
                    <small>{{ client.cpf || client.cnpj || "documento nao informado" }}</small>
                  </div>
                </div>
                <v-autocomplete v-model="selectedClientId" :items="clients" item-value="_id" :item-text="clientSearchText" label="Buscar por nome, CPF ou CNPJ" outlined dense clearable />
                <div v-if="selectedClientToLink" class="appointment-review">
                  <strong>{{ selectedClientToLink.nome || selectedClientToLink.razao_social }}</strong>
                  <small>{{ selectedClientToLink.client_type === 'pj' ? 'Pessoa juridica' : 'Pessoa fisica' }} - {{ selectedClientToLink.cpf || selectedClientToLink.cnpj || 'documento nao informado' }}</small>
                  <small>{{ isSelectedClientAlreadyLinked ? 'Este cliente ja esta vinculado a esta conversa.' : `Vinculo novo: ${conversationPhoneLabel}` }}</small>
                </div>
                <v-btn block color="#1f8f55" dark :disabled="!selectedClientId || isSelectedClientAlreadyLinked" @click="confirmLinkClient">Confirmar vinculo</v-btn>
                <v-btn block outlined color="#1f8f55" @click="openCreateClientFromWhatsapp">
                  <v-icon left small>mdi-account-plus-outline</v-icon>
                  Criar novo cliente com este contato
                </v-btn>
              </section>
              <section v-if="clientPanelView === 'sales'" class="client-detail-section">
                <div class="sales-header">
                  <h3>Historico de compras</h3>
                  <v-spacer />
                  <v-btn icon small :color="hasSalesFilters ? '#1f8f55' : undefined" @click="salesFilterMenu = !salesFilterMenu"><v-icon small>mdi-filter-variant</v-icon></v-btn>
                  <v-btn icon small @click="loadSales"><v-icon small>mdi-refresh</v-icon></v-btn>
                </div>
                <v-card v-if="salesFilterMenu" class="sales-filter-popover" :dark="isDark">
                  <div class="filter-popover-header">
                    <strong>Filtrar vendas</strong>
                    <v-btn icon small @click="salesFilterMenu = false"><v-icon small>mdi-close</v-icon></v-btn>
                  </div>
                  <v-text-field v-model="salesDateFrom" type="date" label="De" dense outlined hide-details />
                  <v-text-field v-model="salesDateTo" type="date" label="Ate" dense outlined hide-details />
                  <div class="filter-actions">
                    <v-btn text small @click="clearSalesFilters">Limpar</v-btn>
                    <v-spacer />
                    <v-btn small color="#1f8f55" dark @click="applySalesFilters">Aplicar</v-btn>
                  </div>
                </v-card>
                <div v-if="sales.length" class="sales-history-list">
                  <SaleItem v-for="sale in sales" :key="sale._id" :kanban="sale" />
                </div>
                <p v-else class="caption mt-3">Nenhuma venda vinculada.</p>
              </section>
              <section v-if="clientPanelView === 'appointments'" class="client-detail-section">
                <v-btn block outlined color="#1f8f55" :loading="appointmentLoading" @click="loadAppointments">Atualizar agendamentos</v-btn>
                <div v-if="appointments.length" class="appointment-list">
                  <div v-for="appointment in appointments" :key="appointment._id" class="appointment-card">
                    <strong>{{ appointment.name || formatAppointmentType(appointment.type) }}</strong>
                    <small>{{ appointment.visit_date }} {{ appointment.visit_hour }}</small>
                    <small>{{ formatAppointmentType(appointment.type) }} - {{ formatAppointmentStatus(appointment.status) }}</small>
                  </div>
                </div>
                <p v-else class="caption mt-3">Nenhum agendamento encontrado para este cliente.</p>
              </section>
              <section v-if="clientPanelView === 'newAppointment'" class="client-detail-section">
                <div class="step-pill">Passo {{ newAppointmentStep }} de 3</div>
                <div v-if="newAppointmentStep === 1" class="appointment-type-grid">
                  <button v-for="type in appointmentTypes" :key="type.value" class="appointment-type-card" :class="{ active: newAppointment.type === type.value }" @click="selectAppointmentType(type.value)">
                    <v-icon>{{ type.icon }}</v-icon>
                    <strong>{{ type.name }}</strong>
                  </button>
                </div>
                <div v-else-if="newAppointmentStep === 2" class="appointment-form">
                  <v-text-field v-model="newAppointment.name" label="Titulo" outlined dense />
                  <v-text-field v-model="newAppointment.visit_date" type="date" label="Data" outlined dense />
                  <v-text-field v-model="newAppointment.visit_hour" type="time" label="Hora" outlined dense />
                  <v-autocomplete v-model="newAppointment.executor" :items="employees" item-value="_id" item-text="nome" label="Executor" outlined dense />
                  <v-select v-if="newAppointment.type === 'delivery'" v-model="newAppointment.sale_id" :items="sales" item-value="_id" :item-text="saleOptionText" label="Venda da entrega" outlined dense />
                </div>
                <div v-else class="appointment-review">
                  <strong>{{ newAppointment.name || formatAppointmentType(newAppointment.type) }}</strong>
                  <small>{{ formatAppointmentType(newAppointment.type) }} para {{ activeLinkedClient && activeLinkedClient.nome }}</small>
                  <small>{{ displayBrazilianDate(newAppointment.visit_date) }} {{ newAppointment.visit_hour }}</small>
                </div>
                <div class="step-actions">
                  <v-btn text :disabled="newAppointmentStep === 1" @click="newAppointmentStep--">Voltar</v-btn>
                  <v-spacer />
                  <v-btn v-if="newAppointmentStep < 3" color="#1f8f55" dark @click="nextAppointmentStep">Avancar</v-btn>
                  <v-btn v-else color="#1f8f55" dark :loading="appointmentLoading" @click="createAppointment">Criar agendamento</v-btn>
                </div>
              </section>
            </div>
          </transition>
          <div v-if="false">
          <h3>Cliente</h3>
          <div v-if="selectedConversation.client" class="linked-client">
            <strong>{{ selectedConversation.client.nome }}</strong>
            <small>{{ selectedConversation.client.telefone }}</small>
          </div>
          <template v-else>
            <p class="caption">Vincule o número desta conversa a um cadastro existente.</p>
            <v-autocomplete v-model="selectedClientId" :items="clients" item-value="_id" :item-text="clientSearchText" label="Buscar por nome, CPF ou telefone" outlined dense />
            <v-btn block color="#1f8f55" dark :disabled="!selectedClientId" @click="linkClient">Vincular cliente</v-btn>
          </template>
          <v-divider class="my-5" />
          <div class="sales-header">
            <h3>Histórico</h3>
            <v-spacer />
            <v-btn icon small :color="hasSalesFilters ? '#1f8f55' : undefined" @click="salesFilterMenu = !salesFilterMenu"><v-icon small>mdi-filter-variant</v-icon></v-btn>
            <v-btn icon small @click="loadSales"><v-icon small>mdi-refresh</v-icon></v-btn>
          </div>
          <v-card v-if="salesFilterMenu" class="sales-filter-popover" :dark="isDark">
            <div class="filter-popover-header">
              <strong>Filtrar vendas</strong>
              <v-btn icon small @click="salesFilterMenu = false"><v-icon small>mdi-close</v-icon></v-btn>
            </div>
            <v-text-field v-model="salesDateFrom" type="date" label="De" dense outlined hide-details />
            <v-text-field v-model="salesDateTo" type="date" label="Até" dense outlined hide-details />
            <div class="filter-actions">
              <v-btn text small @click="clearSalesFilters">Limpar</v-btn>
              <v-spacer />
              <v-btn small color="#1f8f55" dark @click="applySalesFilters">Aplicar</v-btn>
            </div>
          </v-card>
          <div v-if="sales.length" class="sales-history-list">
            <SaleItem v-for="sale in sales" :key="sale._id" :kanban="sale" />
          </div>
          <p v-else class="caption mt-3">Nenhuma venda vinculada.</p>
          </div>
        </aside>
      </v-card>
      <ViewSale />
      <v-dialog v-model="createClientDialog" max-width="520">
        <v-card class="create-whatsapp-client-card" :class="{ dark: isDark }" :dark="isDark">
          <div class="create-client-hero">
            <v-avatar color="#25D366" size="48"><v-icon dark>mdi-whatsapp</v-icon></v-avatar>
            <div>
              <strong>Novo cliente pelo WhatsApp</strong>
              <small>Revise o nome e salve o telefone real da conversa.</small>
            </div>
          </div>
          <v-card-text class="pt-4">
            <v-btn-toggle v-model="newClientDraft.client_type" mandatory rounded dense class="mb-4">
              <v-btn value="pf" small>PF</v-btn>
              <v-btn value="pj" small>PJ</v-btn>
            </v-btn-toggle>
            <v-text-field v-model="newClientDraft.nome" label="Nome do cliente" outlined dense hide-details class="mb-3" />
            <v-text-field v-if="newClientDraft.client_type === 'pj'" v-model="newClientDraft.razao_social" label="Razao social" outlined dense hide-details class="mb-3" />
            <v-text-field v-model="newClientDraft.telefone" label="Telefone WhatsApp" outlined dense hide-details class="mb-3" v-mask="['###############']" />
            <v-text-field v-if="newClientDraft.client_type === 'pf'" v-model="newClientDraft.cpf" label="CPF opcional" outlined dense hide-details class="mb-3" />
            <v-text-field v-else v-model="newClientDraft.cnpj" label="CNPJ opcional" outlined dense hide-details class="mb-3" />
            <v-textarea v-model="newClientDraft.obs" label="Observacao opcional" outlined dense rows="2" hide-details />
          </v-card-text>
          <v-card-actions class="create-client-actions">
            <v-btn text @click="createClientDialog = false">Cancelar</v-btn>
            <v-spacer />
            <v-btn class="create-client-submit" :loading="creatingClient" @click="createClientFromWhatsapp">
              Criar e vincular
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-snackbar v-model="internalNotification.show" right bottom timeout="9000" color="transparent" elevation="0" content-class="internal-chat-snackbar">
        <div class="internal-snackbar-card" :class="{ 'internal-snackbar-card-dark': isDark }" @click="openInternalNotification">
          <div class="internal-snackbar-icon"><v-icon class="internal-snackbar-glyph">mdi-message-text</v-icon></div>
          <v-avatar size="44" class="internal-snackbar-avatar">
            <img v-if="employeeAvatar(internalNotification.user || {})" :src="employeeAvatar(internalNotification.user || {})" alt="">
            <v-icon v-else class="internal-snackbar-glyph">mdi-account</v-icon>
          </v-avatar>
          <div class="internal-snackbar-copy">
            <strong>{{ internalNotification.text }}</strong>
            <small>{{ internalNotification.preview }}</small>
          </div>
          <v-icon class="internal-snackbar-chevron">mdi-chevron-right</v-icon>
        </div>
        <template v-slot:action="{ attrs }">
          <v-btn class="internal-snackbar-action" :class="{ 'internal-snackbar-action-dark': isDark }" icon small v-bind="attrs" @click.stop="internalNotification.show = false">
            <v-icon small :color="isDark ? '#e9edef' : '#173b33'">mdi-close</v-icon>
          </v-btn>
        </template>
      </v-snackbar>
    </v-container>
    </layout>
  </div>
</template>

<script>
import { getWhatsappGatewayStatus, refreshWhatsappGatewayRuntimeConfig, sendWhatsappMessage, stopWhatsappGatewayRuntime } from "../runtime";
import GatewayConfig from "./GatewayConfig.vue";
import SaleItem from "@/apps/GestorDePedido/components/SaleItem.vue";
import ViewSale from "@/apps/GestorDePedido/components/ViewSale.vue";
import { mapGetters } from "vuex";

function currentTenantId() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}") || {};
    return String(user?.activeTenant?._id || user?.activeTenant || user?.tenant?.[0]?._id || user?.tenant?.[0] || "").trim();
  } catch {
    return "";
  }
}

export default {
  components: { GatewayConfig, SaleItem, ViewSale },
  data() {
    return {
      rotas: [{ name: "Inicio", path: "/dashboard" }, { name: "WhatsApp", path: "/whatsapp-atendimento" }],
      conversations: [], selectedConversation: null, messages: [], clients: [], sales: [], appointments: [], employees: [], internalConversations: [], selectedInternalUser: null, internalConversation: null, internalMessages: [],
      selectedClientId: "", search: "", internalSearch: "", dateFrom: "", dateTo: "", salesDateFrom: "", salesDateTo: "", newMessage: "", newInternalMessage: "", loading: false, sending: false, sendingInternal: false, claiming: false, archiving: false, appointmentLoading: false, filterMenu: false, salesFilterMenu: false, errorMessage: "",
      createClientDialog: false, creatingClient: false,
      newClientDraft: { client_type: "pf", nome: "", razao_social: "", telefone: "", cpf: "", cnpj: "", obs: "" },
      recording: false, mediaRecorder: null, audioChunks: [], conversationRequestSeq: 0,
      gatewayStatus: getWhatsappGatewayStatus(),
      sidebarTab: 0,
      internalNotification: { show: false, text: "", preview: "", user: null },
      clientPanelView: "menu",
      newAppointmentStep: 1,
      newAppointment: { type: "visit", name: "", visit_date: "", visit_hour: "", executor: "", sale_id: "" },
      appointmentTypes: [
        { name: "Visita", value: "visit", icon: "mdi-calendar-account" },
        { name: "Entrega", value: "delivery", icon: "mdi-truck-delivery-outline" },
        { name: "Data limite", value: "limit_date", icon: "mdi-calendar-clock" },
        { name: "Whatsapp", value: "whatsapp", icon: "mdi-whatsapp" },
        { name: "Outros", value: "other", icon: "mdi-dots-horizontal-circle-outline" }
      ]
    };
  },
  computed: {
    ...mapGetters(["getLoggedUser"]),
    isGatewayConnected() {
      return this.gatewayStatus === "connected";
    },
    shouldShowGatewayConfig() {
      return ["not_configured", "auth_failed", "network_mismatch"].includes(this.gatewayStatus);
    },
    gatewayStatusLabel() {
      return {
        connected: "Conectado",
        connecting: "Conectando",
        unreachable: "Gateway inacessível",
        disconnected: "Reconectando",
        error: "Reconectando"
      }[this.gatewayStatus] || "Conectando";
    },
    gatewayStatusChipColor() {
      if (this.gatewayStatus === "connected") return "#1f8f55";
      if (this.gatewayStatus === "unreachable") return "#ef4444";
      return "#f59e0b";
    },
    isWhatsappTab() {
      return this.normalizedSidebarTab === "whatsapp";
    },
    normalizedSidebarTab() {
      if (this.sidebarTab === "#internal" || this.sidebarTab === "internal" || this.sidebarTab === 1) return "internal";
      return "whatsapp";
    },
    filteredConversations() {
      const search = this.search.toLowerCase();
      return this.conversations.filter(item => `${this.linkedClientsText(item)} ${item.client?.nome || ""} ${item.client?.cpf || ""} ${item.contact_name || ""} ${this.conversationPhoneLabelFor(item)} ${item.chat_id || ""}`.toLowerCase().includes(search));
    },
    filteredEmployees() {
      const search = this.internalSearch.toLowerCase();
      return this.employees
        .filter(employee => String(employee._id) !== this.currentUserId)
        .filter(employee => `${employee.nome || ""} ${employee.email || ""} ${employee.telefone || ""}`.toLowerCase().includes(search));
    },
    isDark() {
      return localStorage.dark === "true";
    },
    isAssignedToAnotherUser() {
      return Boolean(this.selectedConversation?.assigned_user && !this.isAssignedToMe);
    },
    isAssignedToMe() {
      return this.isConversationAssignedToCurrentUser(this.selectedConversation);
    },
    composerPlaceholder() {
      return this.isAssignedToAnotherUser ? `Atendimento assumido por ${this.selectedConversation.assigned_user.nome}` : "Digite uma mensagem";
    },
    hasFilters() {
      return Boolean(this.dateFrom || this.dateTo);
    },
    hasSalesFilters() {
      return Boolean(this.salesDateFrom || this.salesDateTo);
    },
    clientPanelOptions() {
      return [
        { view: "attendance", icon: "mdi-headset", title: "Assumir atendimento", description: "Responsavel e linha do tempo do atendimento" },
        { view: "linkClient", icon: "mdi-link-variant", title: "Vincular cliente", description: "Buscar cliente interno e salvar o numero certo" },
        { view: "sales", icon: "mdi-receipt-text-outline", title: "Historico de compras", description: "Vendas vinculadas ao cliente" },
        { view: "appointments", icon: "mdi-calendar-month-outline", title: "Agendamentos", description: "Visitas, entregas e tarefas do cliente" },
        { view: "newAppointment", icon: "mdi-calendar-plus", title: "Novo agendamento", description: "Criar pelo fluxo guiado da agenda" }
      ];
    },
    clientPanelTitle() {
      const option = this.clientPanelOptions.find(item => item.view === this.clientPanelView);
      return option ? option.title : "Cliente";
    },
    attendanceHistoryItems() {
      const history = Array.isArray(this.selectedConversation?.attendance_history) ? this.selectedConversation.attendance_history : [];
      const mapped = history.map((item, index) => ({
        id: item._id || `${item.type || "history"}-${index}`,
        title: item.type === "assigned" ? "Atendimento assumido" : "Atualizacao do atendimento",
        description: item.user?.nome ? `${item.user.nome} ficou responsavel pela conversa` : "Movimentacao registrada",
        date: item.created_at
      }));
      if (this.selectedConversation?.created_at) {
        mapped.push({
          id: "created",
          title: "Conversa iniciada",
          description: this.conversationName(this.selectedConversation),
          date: this.selectedConversation.created_at
        });
      }
      return mapped.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
    },
    selectedClientToLink() {
      return this.clients.find(client => String(client._id) === String(this.selectedClientId)) || null;
    },
    linkedClients() {
      return this.linkedClientsForConversation(this.selectedConversation);
    },
    activeLinkedClient() {
      return this.linkedClients[0] || null;
    },
    isSelectedClientAlreadyLinked() {
      return this.linkedClients.some(client => String(client._id) === String(this.selectedClientId));
    },
    conversationPhoneLabel() {
      return this.conversationPhoneLabelFor(this.selectedConversation, this.messages);
    },
    currentUserId() {
      const token = localStorage.token ? JSON.parse(localStorage.token) : "";
      if (!token) return "";
      try {
        const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
        const payload = JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "=")));
        return String(payload.id || "");
      } catch {
        return "";
      }
    }
  },
  methods: {
    normalizeSidebarTab(value) {
      if (value === "#internal" || value === "internal" || value === 1) return "internal";
      return "whatsapp";
    },
    linkedClientsForConversation(conversation) {
      const clients = Array.isArray(conversation?.linked_clients) ? conversation.linked_clients : [];
      const fallbackClient = conversation?.client ? [conversation.client] : [];
      const seen = new Set();
      return [...clients, ...fallbackClient].filter(client => {
        const id = String(client?._id || "");
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
    },
    linkedClientsText(conversation) {
      return this.linkedClientsForConversation(conversation)
        .map(client => `${client.nome || ""} ${client.cpf || ""} ${client.cnpj || ""} ${client.telefone || ""}`)
        .join(" ");
    },
    onlyDigits(value) {
      return String(value || "").replace(/\D/g, "");
    },
    setSidebarTab(value) {
      this.sidebarTab = this.normalizeSidebarTab(value) === "internal" ? 1 : 0;
    },
    async loadConversations({ refreshSelected = false } = {}) {
      this.loading = true;
      try {
        const response = await this.$run("whatsapp-conversations/list", { limit: 100, date_from: this.dateFrom, date_to: this.dateTo });
        this.conversations = (response.data.conversations || []).filter(item => this.shouldShowConversation(item));
        const requestedId = this.$route.query.conversationId;
        const currentId = this.selectedConversation?._id || requestedId;
        const updated = this.conversations.find(item => String(item._id) === String(currentId));
        if (updated && (!this.selectedConversation || refreshSelected)) {
          await this.openConversation(updated);
        } else if (updated) {
          this.selectedConversation = { ...this.selectedConversation, ...updated };
        }
      } finally { this.loading = false; }
    },
    async loadClients() {
      const response = await this.$run("clientes/list", { nome: "" });
      this.clients = response.data.docs || [];
    },
    async loadEmployees() {
      const response = await this.$run("funcionarios/list", { nome: "" });
      this.employees = this.mergeInternalUsers(response.data.docs || []);
      this.openInternalChatFromRoute();
    },
    mergeInternalUsers(users = []) {
      const merged = [];
      const addUser = user => {
        const id = user?._id || user?.id;
        if (!id || merged.some(item => String(item._id) === String(id))) return;
        merged.push({
          ...user,
          _id: id,
          nome: user.nome || user.name || user.email || "Administrador"
        });
      };
      users.forEach(addUser);
      try {
        const loggedUser = this.getLoggedUser || JSON.parse(localStorage.user || "{}");
        const tenants = [
          ...(Array.isArray(loggedUser.tenant) ? loggedUser.tenant : []),
          loggedUser.activeTenant
        ].filter(Boolean);
        tenants.forEach(tenant => {
          addUser(tenant.admin);
          (tenant.admins || []).forEach(addUser);
          (tenant.users || []).forEach(addUser);
        });
        if (loggedUser.admin || loggedUser.is_admin) addUser(loggedUser);
      } catch {
      }
      return merged;
    },
    async loadInternalConversations() {
      const response = await this.$run("internal-chat/list", {});
      this.internalConversations = response.data.conversations || [];
    },
    async openConversation(conversation) {
      this.selectedInternalUser = null;
      const requestId = ++this.conversationRequestSeq;
      this.selectedConversation = conversation;
      this.messages = [];
      this.sales = [];
      this.appointments = [];
      this.clientPanelView = "menu";
      this.resetAppointmentFlow();
      this.syncConversationRoute(conversation._id);
      const response = await this.$run("whatsapp-conversations/view", { conversation_id: conversation._id });
      if (requestId !== this.conversationRequestSeq) return;
      this.selectedConversation = response.data.conversation;
      this.messages = this.deduplicateMessages(response.data.messages || []);
      this.scrollMessagesToEnd();
      void this.$run("whatsapp-conversations/mark-as-read", { conversation_id: conversation._id });
      void this.loadSales();
    },
    async openInternalChat(employee) {
      this.selectedConversation = null;
      this.messages = [];
      this.sales = [];
      this.selectedInternalUser = employee;
      this.internalMessages = [];
      this.errorMessage = "";
      const response = await this.$run("internal-chat/view", { peer_id: employee._id });
      this.internalConversation = response.data.conversation;
      this.internalMessages = response.data.messages || [];
      this.upsertInternalConversation(response.data.conversation);
      this.scrollInternalMessagesToEnd();
      void this.$run("internal-chat/mark-as-read", { conversation_id: response.data.conversation._id });
    },
    openInternalChatFromRoute() {
      const internalUserId = this.$route.query.internalUserId;
      if (!internalUserId) return;
      this.setSidebarTab("internal");
      if (!this.employees.length) return;
      const employee = this.employees.find(item => String(item._id) === String(internalUserId));
      if (employee) {
        if (String(this.selectedInternalUser?._id || "") === String(employee._id)) return;
        void this.openInternalChat(employee);
      }
    },
    async claimConversation() {
      this.claiming = true;
      this.errorMessage = "";
      try {
        const response = await this.$run("whatsapp-conversations/assign", { conversation_id: this.selectedConversation._id });
        this.selectedConversation = response.data.conversation;
        this.upsertConversation(response.data.conversation);
        return true;
      } catch (error) {
        this.showError(error);
        return false;
      } finally {
        this.claiming = false;
      }
    },
    async archiveConversation() {
      if (!this.selectedConversation?._id || this.archiving) return;
      const confirmed = window.confirm("Arquivar esta conversa? Ela sairá da listagem de atendimentos.");
      if (!confirmed) return;
      this.archiving = true;
      this.errorMessage = "";
      try {
        const archivedId = this.selectedConversation._id;
        await this.$run("whatsapp-conversations/archive", { conversation_id: archivedId });
        this.conversations = this.conversations.filter(item => item._id !== archivedId);
        this.selectedConversation = null;
        this.messages = [];
        this.sales = [];
        this.clearConversationRoute();
      } catch (error) {
        this.showError(error);
      } finally {
        this.archiving = false;
      }
    },
    disconnectGateway() {
      const confirmed = window.confirm("Desconectar o gateway WhatsApp?");
      if (!confirmed) return;
      stopWhatsappGatewayRuntime();
    },
    async linkClient(extraPayload = {}) {
      this.errorMessage = "";
      try {
        const response = await this.$run("whatsapp-conversations/link-client", {
          conversation_id: this.selectedConversation._id,
          client_id: this.selectedClientId,
          ...extraPayload
        });
        this.selectedConversation = response.data.conversation;
        this.upsertConversation(response.data.conversation);
        await this.loadSales();
        await this.loadAppointments();
      } catch (error) {
        this.showError(error);
      }
    },
    openCreateClientFromWhatsapp() {
      if (!this.selectedConversation?._id) return;
      const phone = this.conversationRealPhone(this.selectedConversation, this.messages);
      const phoneObs = phone || "informe o telefone manualmente";
      this.newClientDraft = {
        client_type: "pf",
        nome: this.suggestClientNameFromConversation(),
        razao_social: "",
        telefone: phone,
        cpf: "",
        cnpj: "",
        obs: `Cliente criado a partir da conversa WhatsApp ${phoneObs}.`
      };
      this.createClientDialog = true;
    },
    suggestClientNameFromConversation() {
      const name = String(this.selectedConversation?.contact_name || "").trim();
      const phone = this.conversationRealPhone(this.selectedConversation, this.messages);
      if (!name || name === phone || name.includes("@")) return "";
      return name;
    },
    conversationRealPhone(conversation = this.selectedConversation, messages = this.messages) {
      return this.realPhoneFromConversation(conversation, messages);
    },
    async createClientFromWhatsapp() {
      if (!this.selectedConversation?._id) return;
      if (!this.newClientDraft.nome || !this.onlyDigits(this.newClientDraft.telefone)) {
        this.$store.dispatch("createGlobalMessage", {
          type: "warning",
          message: "Informe o nome e o telefone WhatsApp para criar o cliente.",
          timeout: 4000
        });
        return;
      }
      this.creatingClient = true;
      this.errorMessage = "";
      try {
        const phone = this.onlyDigits(this.newClientDraft.telefone);
        const payload = {
          client_type: this.newClientDraft.client_type,
          nome: this.newClientDraft.nome,
          razao_social: this.newClientDraft.client_type === "pj" ? this.newClientDraft.razao_social || this.newClientDraft.nome : "",
          telefone: phone,
          cpf: this.newClientDraft.client_type === "pf" ? this.newClientDraft.cpf : "",
          cnpj: this.newClientDraft.client_type === "pj" ? this.newClientDraft.cnpj : "",
          obs: this.newClientDraft.obs,
          whatsapp_links: [{
            phone,
            telefone: phone,
            label: this.selectedConversation.contact_name || "WhatsApp",
            chat_id: this.selectedConversation.chat_id,
            conversation_id: String(this.selectedConversation._id),
            linked_at: new Date()
          }]
        };
        const response = await this.$run("clientes/create", payload);
        const client = response.data;
        this.clients = [client, ...this.clients.filter(item => String(item._id) !== String(client._id))];
        this.selectedClientId = client._id;
        await this.linkClient({ contact_phone: phone });
        this.createClientDialog = false;
        this.clientPanelView = "menu";
        this.$store.dispatch("createGlobalMessage", { type: "success", message: "Cliente criado e vinculado ao WhatsApp.", timeout: 3000 });
      } catch (error) {
        this.showError(error);
      } finally {
        this.creatingClient = false;
      }
    },
    async confirmLinkClient() {
      if (!this.selectedClientId || !this.selectedClientToLink) return;
      const confirmed = window.confirm(`Vincular ${this.conversationPhoneLabel} ao cliente ${this.selectedClientToLink.nome || this.selectedClientToLink.razao_social}?`);
      if (!confirmed) return;
      await this.linkClient();
      if (!this.errorMessage) {
        this.$store.dispatch("createGlobalMessage", { type: "success", message: "Cliente vinculado ao WhatsApp.", timeout: 2500 });
        this.clientPanelView = "menu";
      }
    },
    async loadSales() {
      this.sales = [];
      if (!this.linkedClients.length) return;
      const responses = await Promise.all(this.linkedClients.map(client => (
        this.$run("clientes/list-client-sales", { _id: client._id, date_from: this.salesDateFrom, date_to: this.salesDateTo })
      )));
      const seen = new Set();
      this.sales = responses
        .flatMap(response => response.data || [])
        .filter(sale => {
          const id = String(sale?._id || "");
          if (!id || seen.has(id)) return false;
          seen.add(id);
          return true;
        });
    },
    async applySalesFilters() {
      this.salesFilterMenu = false;
      await this.loadSales();
    },
    async clearSalesFilters() {
      this.salesDateFrom = "";
      this.salesDateTo = "";
      this.salesFilterMenu = false;
      await this.loadSales();
    },
    openClientPanelView(view) {
      this.clientPanelView = view;
      this.salesFilterMenu = false;
      if (view === "sales") void this.loadSales();
      if (view === "appointments") void this.loadAppointments();
      if (view === "newAppointment") {
        this.resetAppointmentFlow();
        if (!this.sales.length) void this.loadSales();
      }
    },
    backClientPanel() {
      this.clientPanelView = "menu";
      this.salesFilterMenu = false;
    },
    resetAppointmentFlow() {
      this.newAppointmentStep = 1;
      this.newAppointment = { type: "visit", name: "", visit_date: "", visit_hour: "", executor: this.currentUserId, sale_id: "" };
    },
    selectAppointmentType(type) {
      this.newAppointment = { ...this.newAppointment, type, sale_id: type === "delivery" ? this.newAppointment.sale_id : "" };
      this.newAppointmentStep = 2;
    },
    nextAppointmentStep() {
      if (this.newAppointmentStep === 1 && !this.newAppointment.type) return;
      if (this.newAppointmentStep === 2 && !this.validateAppointmentDraft()) return;
      this.newAppointmentStep += 1;
    },
    validateAppointmentDraft() {
      if (!this.activeLinkedClient?._id) {
        this.errorMessage = "Vincule um cliente antes de criar agendamentos.";
        return false;
      }
      if (!this.newAppointment.executor || !this.newAppointment.visit_date) {
        this.errorMessage = "Informe executor e data do agendamento.";
        return false;
      }
      if (this.newAppointment.type === "delivery" && !this.newAppointment.sale_id) {
        this.errorMessage = "Selecione a venda desta entrega.";
        return false;
      }
      this.errorMessage = "";
      return true;
    },
    async loadAppointments() {
      this.appointments = [];
      if (!this.linkedClients.length) return;
      this.appointmentLoading = true;
      try {
        const response = await this.$run("external_movimentations/list", {});
        const clientIds = new Set(this.linkedClients.map(client => String(client._id || "")).filter(Boolean));
        const clientCpfs = new Set(this.linkedClients.map(client => String(client.cpf || "")).filter(Boolean));
        this.appointments = (response.data || []).filter(item => {
          const appointmentClient = item.client || {};
          return clientIds.has(String(appointmentClient._id || "")) || clientCpfs.has(String(appointmentClient.cpf || ""));
        });
      } catch (error) {
        this.showError(error);
      } finally {
        this.appointmentLoading = false;
      }
    },
    async createAppointment() {
      if (!this.validateAppointmentDraft()) return;
      this.appointmentLoading = true;
      try {
        const sale = this.sales.find(item => String(item._id) === String(this.newAppointment.sale_id));
        const payload = {
          type: this.newAppointment.type,
          name: this.newAppointment.name || this.formatAppointmentType(this.newAppointment.type),
          visit_date: this.displayBrazilianDate(this.newAppointment.visit_date),
          visit_hour: this.newAppointment.visit_hour,
          executor: this.newAppointment.executor,
          client: this.activeLinkedClient
        };
        if (this.newAppointment.type === "delivery") {
          payload.transaction = this.newAppointment.sale_id;
          payload.address_destiny = sale?.compra?.address || sale?.address_destiny || {};
        }
        const useCase = this.newAppointment.type === "delivery" ? "external_movimentations/create-from-transaction" : "external_movimentations/create-from-calendar";
        await this.$run(useCase, payload);
        this.$store.dispatch("createGlobalMessage", { type: "success", message: "Agendamento criado.", timeout: 2500 });
        this.resetAppointmentFlow();
        this.clientPanelView = "appointments";
        await this.loadAppointments();
      } catch (error) {
        this.showError(error);
      } finally {
        this.appointmentLoading = false;
      }
    },
    syncConversationRoute(conversationId) {
      if (!conversationId || this.$route.query.conversationId === conversationId) return;
      this.$router.replace({ path: this.$route.path, query: { ...this.$route.query, conversationId } }).catch(() => {});
    },
    clearConversationRoute() {
      if (!this.$route.query.conversationId) return;
      const query = { ...this.$route.query };
      delete query.conversationId;
      this.$router.replace({ path: this.$route.path, query }).catch(() => {});
    },
    upsertConversation(conversation) {
      if (!conversation?._id) return;
      if (!this.shouldShowConversation(conversation)) {
        this.conversations = this.conversations.filter(item => String(item._id) !== String(conversation._id));
        return;
      }
      const index = this.conversations.findIndex(item => item._id === conversation._id);
      if (index === -1) {
        this.conversations = [conversation, ...this.conversations];
        return;
      }
      const next = [...this.conversations];
      next.splice(index, 1);
      this.conversations = [{ ...this.conversations[index], ...conversation }, ...next];
    },
    messageIdentifiers(message) {
      return [
        message?._id,
        message?.id,
        message?.whatsapp_message_id,
        message?.message_id,
        message?.client_message_id,
        message?.clientMessageId,
        message?.local_message_id
      ].map(value => String(value || "").trim()).filter(Boolean);
    },
    messageKey(message) {
      return this.messageIdentifiers(message)[0] || "";
    },
    messageTimestamp(message) {
      const value = message?.timestamp || message?.created_at || message?.createdAt || message?.sent_at || message?.sentAt;
      const time = new Date(value || 0).getTime();
      return Number.isFinite(time) ? time : 0;
    },
    sameOutgoingMessage(left, right) {
      const leftOutgoing = left?.from_me === true || left?.is_from_me === true;
      const rightOutgoing = right?.from_me === true || right?.is_from_me === true;
      if (!leftOutgoing || !rightOutgoing) return false;
      if (String(left?.message_type || "text") !== String(right?.message_type || "text")) return false;
      const leftText = String(left?.text || "").trim();
      const rightText = String(right?.text || "").trim();
      if (leftText !== rightText) return false;
      const leftTime = this.messageTimestamp(left);
      const rightTime = this.messageTimestamp(right);
      return Boolean(leftTime && rightTime && Math.abs(leftTime - rightTime) <= 2 * 60 * 1000);
    },
    isSameMessage(left, right) {
      const leftIds = this.messageIdentifiers(left);
      const rightIds = this.messageIdentifiers(right);
      if (leftIds.some(id => rightIds.includes(id))) return true;
      return this.sameOutgoingMessage(left, right);
    },
    mergeMessage(existing, incoming) {
      return {
        ...existing,
        ...incoming,
        _id: existing?._id || incoming?._id,
        id: existing?.id || incoming?.id,
        whatsapp_message_id: incoming?.whatsapp_message_id || existing?.whatsapp_message_id,
        message_id: incoming?.message_id || existing?.message_id
      };
    },
    deduplicateMessages(messages = []) {
      return messages.reduce((merged, message) => {
        if (!message || !this.messageKey(message)) return merged;
        const index = merged.findIndex(item => this.isSameMessage(item, message));
        if (index === -1) return [...merged, message];
        const next = [...merged];
        next.splice(index, 1, this.mergeMessage(next[index], message));
        return next;
      }, []);
    },
    upsertMessage(message) {
      if (!message || !this.messageKey(message)) return;
      const index = this.messages.findIndex(item => this.isSameMessage(item, message));
      if (index === -1) {
        this.messages.push(message);
        return;
      }
      const next = [...this.messages];
      next.splice(index, 1, this.mergeMessage(next[index], message));
      this.messages = next;
    },
    isConversationAssignedToCurrentUser(conversation) {
      const assignedUserId = conversation?.assigned_user?._id || conversation?.assigned_user || conversation?.assigned_user_id;
      return Boolean(assignedUserId && String(assignedUserId) === this.currentUserId);
    },
    shouldShowConversation(conversation) {
      if (!conversation?._id) return false;
      if (!this.isCustomerConversation(conversation)) return false;
      if (String(conversation._id) === String(this.selectedConversation?._id || "")) return true;
      return true;
    },
    handleWhatsappOffer(event) {
      const conversation = event.detail?.conversation;
      if (conversation) this.upsertConversation(conversation);
    },
    handleWhatsappAssigned(event) {
      const conversation = event.detail?.conversation;
      if (!conversation?._id) return;
      this.upsertConversation(conversation);
      if (String(conversation._id) === String(this.selectedConversation?._id || "")) {
        this.selectedConversation = { ...this.selectedConversation, ...conversation };
      }
    },
    handleIncomingMessage(event) {
      const detail = event.detail || {};
      const conversation = detail.conversation;
      const message = detail.message;
      if (conversation && this.shouldShowConversation(conversation)) this.upsertConversation(conversation);
      if (!conversation?._id || String(conversation._id) !== String(this.selectedConversation?._id || "") || !message?._id) return;
      this.upsertMessage(message);
      this.selectedConversation = { ...this.selectedConversation, ...conversation, unread_count: message.from_me ? this.selectedConversation.unread_count : 0 };
      this.scrollMessagesToEnd();
      if (!message.from_me) {
        void this.$run("whatsapp-conversations/mark-as-read", { conversation_id: conversation._id });
      }
    },
    handleMessageStatus(event) {
      const message = event.detail?.message;
      if (!message || !this.messageKey(message)) return;
      if (!this.messages.some(item => this.isSameMessage(item, message))) return;
      this.upsertMessage(message);
    },
    async handleQueuedTemplate(event) {
      const message = event.detail?.message;
      const conversation = event.detail?.conversation;
      if (!message?._id || !conversation?.chat_id) return;
      try {
        const payload = {
          chatId: conversation.chat_id,
          clientMessageId: message._id,
          text: message.text || "",
          messageType: message.message_type || "text",
        };
        if (message.message_type === "poll") payload.poll = this.pollForGateway(message.poll);
        await sendWhatsappMessage(payload);
        if (conversation && this.shouldShowConversation(conversation)) this.upsertConversation(conversation);
        if (String(conversation._id || "") === String(this.selectedConversation?._id || "")) {
          this.upsertMessage(message);
          this.selectedConversation = { ...this.selectedConversation, ...conversation };
          this.scrollMessagesToEnd();
        }
      } catch (error) {
        this.showError(error);
      }
    },
    handleContactPicture(event) {
      const conversation = event.detail?.conversation;
      if (!conversation?._id) return;
      if (this.shouldShowConversation(conversation)) this.upsertConversation(conversation);
      if (conversation._id === this.selectedConversation?._id) {
        this.selectedConversation = { ...this.selectedConversation, ...conversation };
      }
    },
    handleUserPresence(event) {
      const user = event.detail?.user;
      if (!user?._id) return;
      const index = this.employees.findIndex(item => String(item._id) === String(user._id));
      if (index === -1) {
        this.employees = [user, ...this.employees];
        return;
      }
      const next = [...this.employees];
      next.splice(index, 1, { ...next[index], ...user });
      this.employees = next;
      if (String(this.selectedInternalUser?._id || "") === String(user._id)) {
        this.selectedInternalUser = { ...this.selectedInternalUser, ...user };
      }
    },
    handleInternalMessage(event) {
      const detail = event.detail || {};
      if (!this.isInternalChatForCurrentUser(detail)) return;
      const conversation = detail.conversation;
      const message = detail.message;
      if (conversation) this.upsertInternalConversation(conversation);
      if (!message?._id || !conversation?._id) return;
      const selectedId = String(this.internalConversation?._id || "");
      const isSelectedConversation = String(conversation._id) === selectedId;
      if (!isSelectedConversation) {
        if (!this.isInternalMessageMine(message)) {
          this.notifyInternalMessage(message);
        }
        return;
      }
      if (!this.internalMessages.some(item => item._id === message._id)) {
        this.internalMessages.push(message);
      }
      this.internalConversation = { ...this.internalConversation, ...conversation };
      this.scrollInternalMessagesToEnd();
      if (!this.isInternalMessageMine(message)) {
        this.playInternalNotificationSound();
        void this.$run("internal-chat/mark-as-read", { conversation_id: conversation._id });
      }
    },
    notifyInternalMessage(message) {
      if (!message?._id) return;
      this.internalNotification.show = false;
    },
    playInternalNotificationSound() {
      const audio = new Audio("call_in.mp3");
      audio.volume = 0.55;
      void audio.play().catch(() => {
        this.playInternalBeepFallback();
      });
    },
    playInternalBeepFallback() {
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;
        const context = new AudioContext();
        const gain = context.createGain();
        gain.gain.setValueAtTime(0.0001, context.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.08, context.currentTime + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.22);
        gain.connect(context.destination);
        [660, 880].forEach((frequency, index) => {
          const oscillator = context.createOscillator();
          oscillator.type = "sine";
          oscillator.frequency.setValueAtTime(frequency, context.currentTime + index * 0.09);
          oscillator.connect(gain);
          oscillator.start(context.currentTime + index * 0.09);
          oscillator.stop(context.currentTime + index * 0.09 + 0.12);
        });
        setTimeout(() => context.close(), 450);
      } catch {
      }
    },
    openInternalNotification() {
      const user = this.internalNotification.user;
      this.internalNotification.show = false;
      if (user?._id) {
        this.setSidebarTab("internal");
        void this.openInternalChat(user);
      }
    },
    upsertInternalConversation(conversation) {
      if (!conversation?._id) return;
      const index = this.internalConversations.findIndex(item => item._id === conversation._id);
      if (index === -1) {
        this.internalConversations = [conversation, ...this.internalConversations];
        return;
      }
      const next = [...this.internalConversations];
      next.splice(index, 1);
      this.internalConversations = [{ ...this.internalConversations[index], ...conversation }, ...next];
    },
    async sendInternalMessage() {
      const text = this.newInternalMessage.trim();
      if (!text || !this.selectedInternalUser?._id) return;
      this.sendingInternal = true;
      this.errorMessage = "";
      try {
        const response = await this.$run("internal-chat/send-message", { peer_id: this.selectedInternalUser._id, text });
        this.internalConversation = response.data.conversation;
        this.upsertInternalConversation(response.data.conversation);
        if (!this.internalMessages.some(item => item._id === response.data.message._id)) {
          this.internalMessages.push(response.data.message);
        }
        this.newInternalMessage = "";
        this.scrollInternalMessagesToEnd();
      } catch (error) {
        this.showError(error);
      } finally {
        this.sendingInternal = false;
      }
    },
    async sendMessage() {
      const text = this.newMessage.trim();
      if (!text) return;
      this.sending = true;
      this.errorMessage = "";
      try {
        if (!this.selectedConversation.assigned_user && !(await this.claimConversation())) return;
        const response = await this.createOutgoingMessage({ text, message_type: "text" });
        await this.sendOutgoingToGateway(response.data.message, { text, messageType: "text" });
        this.upsertMessage(response.data.message);
        this.upsertConversation(response.data.conversation);
        this.selectedConversation = { ...this.selectedConversation, ...response.data.conversation };
        this.scrollMessagesToEnd();
        this.newMessage = "";
      } catch (error) {
        this.showError(error);
      } finally { this.sending = false; }
    },
    async ensureClaimed() {
      return this.selectedConversation.assigned_user || this.claimConversation();
    },
    createOutgoingMessage(payload) {
      return this.$run("whatsapp-conversations/send-message", { conversation_id: this.selectedConversation._id, ...payload });
    },
    sendOutgoingToGateway(message, payload) {
      return sendWhatsappMessage({ chatId: this.selectedConversation.chat_id, clientMessageId: message._id, ...payload });
    },
    async sendDocument(event) {
      const file = event.target.files[0];
      event.target.value = "";
      if (!file || !(await this.ensureClaimed())) return;
      try {
        const attachment = { name: file.name, mimeType: file.type || "application/octet-stream", data: await this.fileToBase64(file) };
        const messageType = attachment.mimeType.startsWith("image/") ? "image" : attachment.mimeType.startsWith("video/") ? "video" : "document";
        const response = await this.createOutgoingMessage({ text: file.name, message_type: messageType, attachment: this.attachmentForStorage(attachment) });
        await this.sendOutgoingToGateway(response.data.message, { text: "", messageType, attachment });
        this.upsertMessage(response.data.message);
        this.upsertConversation(response.data.conversation);
        this.selectedConversation = { ...this.selectedConversation, ...response.data.conversation };
        this.scrollMessagesToEnd();
      } catch (error) { this.showError(error); }
    },
    async toggleAudioRecording() {
      if (this.recording) return this.mediaRecorder.stop();
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.audioChunks = [];
        const mimeType = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"].find(type => MediaRecorder.isTypeSupported(type));
        this.mediaRecorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
        this.mediaRecorder.ondataavailable = event => {
          if (event.data && event.data.size > 0) this.audioChunks.push(event.data);
        };
        this.mediaRecorder.onstop = async () => {
          stream.getTracks().forEach(track => track.stop());
          this.recording = false;
          if (!(await this.ensureClaimed())) return;
          const blob = new Blob(this.audioChunks, { type: this.mediaRecorder.mimeType || "audio/webm" });
          if (blob.size < 1024) throw new Error("Áudio muito curto ou inválido. Grave novamente.");
          const extension = blob.type.includes("ogg") ? "ogg" : blob.type.includes("mp4") ? "m4a" : "webm";
          const attachment = { name: `audio-${Date.now()}.${extension}`, mimeType: blob.type, data: await this.fileToBase64(blob) };
          const response = await this.createOutgoingMessage({ text: "Áudio", message_type: "audio", attachment: this.attachmentForStorage(attachment) });
          await this.sendOutgoingToGateway(response.data.message, { messageType: "audio", attachment });
          this.upsertMessage(response.data.message);
          this.upsertConversation(response.data.conversation);
          this.selectedConversation = { ...this.selectedConversation, ...response.data.conversation };
          this.scrollMessagesToEnd();
        };
        this.mediaRecorder.start();
        this.recording = true;
      } catch (error) { this.showError(error); }
    },
    async sendPoll() {
      const name = window.prompt("Pergunta da enquete:");
      if (!name || !(await this.ensureClaimed())) return;
      const values = window.prompt("Opções separadas por vírgula:")?.split(",").map(item => item.trim()).filter(Boolean);
      if (!values || values.length < 2) return;
      try {
        const poll = { name, values };
        const response = await this.createOutgoingMessage({ text: name, message_type: "poll", poll });
        await this.sendOutgoingToGateway(response.data.message, { messageType: "poll", poll });
        this.upsertMessage(response.data.message);
        this.upsertConversation(response.data.conversation);
        this.selectedConversation = { ...this.selectedConversation, ...response.data.conversation };
        this.scrollMessagesToEnd();
      } catch (error) { this.showError(error); }
    },
    async deleteMessage(message) {
      try {
        await this.$run("whatsapp-conversations/delete-message", { message_id: message._id });
        this.messages = this.messages.filter(item => item._id !== message._id);
      } catch (error) { this.showError(error); }
    },
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
    attachmentForStorage(attachment) {
      const stored = { name: attachment.name, mimeType: attachment.mimeType };
      if (attachment.data && attachment.data.length <= 7 * 1024 * 1024) stored.previewDataUrl = `data:${attachment.mimeType};base64,${attachment.data}`;
      return stored;
    },
    pollOptions(message) {
      const poll = message.poll || {};
      const results = Array.isArray(poll.results) ? poll.results : [];
      const values = poll.values || (poll.options || []).map(option => option.text || option.name || option.id).filter(Boolean);
      return values.map(name => ({ name, count: results.find(result => result.name === name || result.text === name)?.count || 0 }));
    },
    pollForGateway(poll = {}) {
      const values = poll.values || (poll.options || []).map(option => option.text || option.name || option.id).filter(Boolean);
      return {
        ...poll,
        name: poll.name || poll.question || "Selecione uma opcao",
        values
      };
    },
    scrollMessagesToEnd() {
      this.$nextTick(() => {
        const stream = this.$refs.messageStream;
        if (stream) stream.scrollTop = stream.scrollHeight;
      });
    },
    scrollInternalMessagesToEnd() {
      this.$nextTick(() => {
        const stream = this.$refs.internalMessageStream;
        if (stream) stream.scrollTop = stream.scrollHeight;
      });
    },
    async applyFilters() {
      this.filterMenu = false;
      await this.loadConversations();
    },
    async clearFilters() {
      this.dateFrom = "";
      this.dateTo = "";
      this.filterMenu = false;
      await this.loadConversations();
    },
    statusIcon(status) {
      return ["delivered", "read"].includes(status) ? "mdi-check-all" : "mdi-check";
    },
    formatDate(value) { return value ? new Date(value).toLocaleString("pt-BR") : ""; },
    formatMoney(value) { return Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }); },
    displayBrazilianDate(value) {
      if (!value || !String(value).includes("-")) return value || "";
      const [year, month, day] = String(value).split("-");
      return `${day}/${month}/${year}`;
    },
    formatAppointmentType(type) {
      const labels = { visit: "Visita", delivery: "Entrega", limit_date: "Data limite", whatsapp: "Whatsapp", other: "Outros" };
      return labels[type] || type || "Agendamento";
    },
    formatAppointmentStatus(status) {
      const labels = { waiting: "Aguardando", on_the_way: "Em andamento", done: "Concluido", canceled: "Cancelado" };
      return labels[status] || status || "Aguardando";
    },
    saleOptionText(sale) {
      return `Venda #${sale.id_transaction || sale.id || sale._id} - ${this.formatMoney(this.saleTotal(sale))}`;
    },
    conversationName(conversation) { return conversation.client?.nome || conversation.contact_name || this.conversationPhoneLabelFor(conversation); },
    conversationPhoneLabelFor(conversation, messages = []) {
      return this.realPhoneFromConversation(conversation, messages) || "telefone nao identificado";
    },
    realPhoneFromConversation(conversation, messages = []) {
      const linkedClients = this.linkedClientsForConversation(conversation);
      const candidates = [
        ...linkedClients.flatMap(client => [client.whatsapp_phone, client.telefone]),
        conversation?.client?.whatsapp_phone,
        conversation?.client?.telefone,
        conversation?.contact_phone,
        conversation?.contact?.phone,
        conversation?.contact?.whatsapp_phone,
        conversation?.phone,
        conversation?.whatsapp_phone,
        conversation?.remoteJid,
        conversation?.remote_jid,
        conversation?.jid,
        conversation?.contact_id,
        conversation?.chat_id,
        ...this.phoneCandidatesFromMessages(messages)
      ];
      const blockedDigits = this.lidDerivedDigits(conversation);
      for (const value of candidates) {
        const phone = this.realPhoneFromIdentifier(value);
        if (phone && !blockedDigits.includes(phone)) return phone;
      }
      return "";
    },
    phoneCandidatesFromMessages(messages = []) {
      return (Array.isArray(messages) ? messages : [])
        .filter(message => !message?.from_me)
        .flatMap(message => [
          message?.contact_phone,
          message?.sender_phone,
          message?.senderPhone,
          message?.from_phone,
          message?.fromPhone,
          message?.wa_id,
          message?.waId,
          message?.contactPhone,
          message?.remoteJid,
          message?.remote_jid,
          message?.jid,
          message?.from,
          message?.to,
          message?.sender_id,
          message?.senderId,
          message?.sender,
          message?.participant,
          message?.author,
          message?.chat_id,
          message?.chatId,
          message?.key?.remoteJid,
          message?.key?.participant,
          message?.raw?.remoteJid,
          message?.raw?.participant,
          message?.raw?.key?.remoteJid,
          message?.raw?.key?.participant,
          message?.rawMessage?.remoteJid,
          message?.rawMessage?.participant,
          message?.rawMessage?.key?.remoteJid,
          message?.rawMessage?.key?.participant,
          message?.sender?.phone,
          message?.sender?.whatsapp_phone
        ]);
    },
    lidDerivedDigits(conversation) {
      return [
        conversation?.chat_id,
        conversation?.contact_id,
        conversation?.contact_phone
      ].filter(value => this.isLidIdentifier(value)).map(value => String(value || "").split("@")[0].replace(/\D/g, "")).filter(Boolean);
    },
    isLidIdentifier(value) {
      return /(^|@|:)lid($|[:@])|@lid$/i.test(String(value || "").trim());
    },
    realPhoneFromIdentifier(value) {
      const raw = String(value || "").trim();
      if (!raw) return "";
      if (this.isLidIdentifier(raw) || /@(g\.us|newsletter|broadcast)$/i.test(raw) || /status@broadcast/i.test(raw)) return "";
      const jidUser = raw.split("@")[0];
      const digits = jidUser.replace(/\D/g, "");
      return digits.length >= 10 && digits.length <= 15 ? digits : "";
    },
    isCustomerConversation(conversation) {
      const type = String(conversation?.chat_type || conversation?.chatType || conversation?.type || "").toLowerCase();
      if (["group", "channel", "newsletter", "broadcast", "status"].includes(type)) return false;
      const identifiers = [
        conversation?.chat_id,
        conversation?.contact_id,
        conversation?.contact_phone
      ].map(value => String(value || "").toLowerCase());
      if (identifiers.some(value => /@(g\.us|newsletter|broadcast)$/.test(value) || value.includes("status@broadcast"))) return false;
      return true;
    },
    conversationAvatar(conversation) {
      return this.clientPhoto(conversation?.client) || conversation?.contact_picture_url || this.shadcnAvatar(conversation);
    },
    clientPhoto(client) {
      if (!client?.photo) return "";
      return Array.isArray(client.photo) ? client.photo[0] || "" : client.photo;
    },
    shadcnAvatar(conversation) {
      const seed = `${conversation?.client?._id || ""}-${conversation?.contact_phone || conversation?.contact_id || conversation?.chat_id || this.conversationName(conversation) || "whatsapp"}`;
      const avatarNumber = (this.hashString(seed) % 5) + 1;
      return `https://ui.shadcn.com/avatars/0${avatarNumber}.png`;
    },
    hashString(value) {
      return String(value || "").split("").reduce((hash, char) => ((hash << 5) - hash + char.charCodeAt(0)) >>> 0, 0);
    },
    conversationInitials(conversation) {
      return this.conversationName(conversation).split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join("").toUpperCase() || "WA";
    },
    employeeAvatar(employee) {
      if (employee.profile_image) return employee.profile_image;
      return Array.isArray(employee.photo) ? employee.photo[0] || "" : employee.photo || "";
    },
    employeeStatusLabel(employee) {
      const status = employee.realtime_status || "offline";
      if (status === "online") return "Disponível";
      if (status === "away") return "Ausente";
      return "Offline";
    },
    internalUnread(employee) {
      const conversation = this.internalConversations.find(item => (item.participant_ids || []).map(String).includes(String(employee._id)));
      return Number(conversation?.unread_by?.[this.currentUserId] || 0);
    },
    isInternalMessageMine(message) {
      return String(message?.sender?._id || "") === this.currentUserId;
    },
    entityId(value) {
      if (!value) return "";
      if (typeof value === "string") return value;
      return value._id || value.id || value.user_id || "";
    },
    collectEntityIds(target, keys) {
      return keys.flatMap(key => {
        const value = target?.[key];
        if (!value) return [];
        return Array.isArray(value) ? value.map(this.entityId) : [this.entityId(value)];
      }).map(String).filter(Boolean);
    },
    internalChatParticipantIds(detail = {}) {
      const conversation = detail.conversation || {};
      const message = detail.message || {};
      return [
        ...this.collectEntityIds(detail, ["participants", "participant_ids", "users"]),
        ...this.collectEntityIds(conversation, ["participants", "participant_ids", "users"]),
        ...this.collectEntityIds(message, ["sender", "recipient", "receiver", "to", "from"]),
        ...this.collectEntityIds(message, ["sender_id", "recipient_id", "receiver_id", "to_user_id", "from_user_id"])
      ].map(String).filter(Boolean);
    },
    isInternalChatForCurrentUser(detail) {
      const participants = [...new Set(this.internalChatParticipantIds(detail))];
      return Boolean(participants.length && participants.includes(this.currentUserId));
    },
    clientSearchText(client) { return `${client.nome || client.razao_social || "Cliente"} - CPF/CNPJ: ${client.cpf || client.cnpj || "nao informado"} - ${client.telefone || "sem telefone"}`; },
    saleTotal(sale) { return sale.compra?.valor_total || sale.total || 0; },
    showError(error) { this.errorMessage = error?.response?.data?.message || error?.message || "Não foi possível concluir a operação."; },
    handleRefresh() { void this.loadConversations(); },
    handleGatewayStatus(event) {
      const status = event.detail?.status || "disconnected";
      const wasDisconnected = !this.isGatewayConnected;
      this.gatewayStatus = status;
      if (status === "connected" && wasDisconnected) {
        void this.loadConversations();
      }
    },
    handleGatewayConfigUpdated(event) {
      const config = event.detail?.whatsapp_gateway_config || {};
      refreshWhatsappGatewayRuntimeConfig(config);
      this.gatewayStatus = getWhatsappGatewayStatus();
    },
    async syncGatewayConfigFromBackend() {
      try {
        const response = await this.$run("marketplaces/get-config-whatsapp-gateway", { tenant_id: currentTenantId() });
        refreshWhatsappGatewayRuntimeConfig(response.data?.whatsapp_gateway_config || {});
        this.gatewayStatus = getWhatsappGatewayStatus();
      } catch {
      }
    }
  },
  watch: {
    sidebarTab(value) {
      const normalized = this.normalizeSidebarTab(value);
      const nextValue = normalized === "internal" ? 1 : 0;
      if (value !== nextValue) {
        this.$nextTick(() => {
          this.sidebarTab = nextValue;
        });
      }
    },
    "$route.query.internalUserId": {
      immediate: true,
      handler() {
        this.openInternalChatFromRoute();
      }
    },
    employees() {
      this.openInternalChatFromRoute();
    }
  },
  created() {
    void this.syncGatewayConfigFromBackend();
    this.loadClients();
    this.loadEmployees();
    this.loadInternalConversations();
    if (this.isGatewayConnected) this.loadConversations();
    window.addEventListener("stonepro:whatsapp-refresh", this.handleRefresh);
    window.addEventListener("stonepro:whatsapp-message", this.handleIncomingMessage);
    window.addEventListener("stonepro:whatsapp-message-status", this.handleMessageStatus);
    window.addEventListener("stonepro:whatsapp-template-queued", this.handleQueuedTemplate);
    window.addEventListener("stonepro:whatsapp-offer", this.handleWhatsappOffer);
    window.addEventListener("stonepro:whatsapp-assigned", this.handleWhatsappAssigned);
    window.addEventListener("stonepro:whatsapp-contact-picture", this.handleContactPicture);
    window.addEventListener("stonepro:whatsapp-gateway-status", this.handleGatewayStatus);
    window.addEventListener("stonepro:whatsapp-gateway-config-updated", this.handleGatewayConfigUpdated);
    window.addEventListener("stonepro:user-presence", this.handleUserPresence);
    window.addEventListener("stonepro:internal-chat-message", this.handleInternalMessage);
  },
  beforeDestroy() {
    window.removeEventListener("stonepro:whatsapp-refresh", this.handleRefresh);
    window.removeEventListener("stonepro:whatsapp-message", this.handleIncomingMessage);
    window.removeEventListener("stonepro:whatsapp-message-status", this.handleMessageStatus);
    window.removeEventListener("stonepro:whatsapp-template-queued", this.handleQueuedTemplate);
    window.removeEventListener("stonepro:whatsapp-offer", this.handleWhatsappOffer);
    window.removeEventListener("stonepro:whatsapp-assigned", this.handleWhatsappAssigned);
    window.removeEventListener("stonepro:whatsapp-contact-picture", this.handleContactPicture);
    window.removeEventListener("stonepro:whatsapp-gateway-status", this.handleGatewayStatus);
    window.removeEventListener("stonepro:whatsapp-gateway-config-updated", this.handleGatewayConfigUpdated);
    window.removeEventListener("stonepro:user-presence", this.handleUserPresence);
    window.removeEventListener("stonepro:internal-chat-message", this.handleInternalMessage);
  }
};
</script>

<style scoped>
.whatsapp-workspace { display: grid; grid-template-columns: minmax(240px, 3fr) minmax(360px, 5fr) minmax(320px, 4fr); grid-template-rows: minmax(0, 1fr); align-items: stretch; height: calc(100vh - 2px); max-height: calc(100vh - 2px); overflow: hidden; border-radius: 0; }
.conversation-sidebar,.client-panel { align-self: stretch; min-height: 0; height: 100%; overflow: hidden; background: #fff; border-right: 1px solid #e5ece8; }.conversation-sidebar { position: relative; display: flex; flex-direction: column; justify-content: flex-start; }.client-panel { position: relative; overflow: auto; border-right: 0; border-left: 1px solid #e5ece8; padding: 18px; }
.sidebar-header,.chat-header,.composer { display: flex; align-items: center; padding: 16px; gap: 8px; }.sidebar-header { flex: 0 0 auto; flex-wrap: wrap; align-items: flex-start; }.sidebar-header h2 { margin: 0; line-height: 1.1; }.sidebar-header small { display: block; margin-top: 3px; }.sidebar-header > div:first-child { flex: 1 1 100%; min-width: 0; }.sidebar-header .v-btn,.sidebar-header .v-chip { flex: 0 0 auto; }.conversation-tabs { flex: 0 0 auto; padding: 0 12px 8px; }.composer { flex: 0 0 auto; padding: 18px 22px 24px; min-height: 82px; }.conversation-search { flex: 0 0 auto; margin: 0 !important; padding: 0 12px 8px !important; }.conversation-search ::v-deep .v-input__control,.conversation-search ::v-deep .v-input__slot { min-height: 38px !important; margin-bottom: 0 !important; }.conversation-search ::v-deep .v-text-field__details { display: none !important; }.conversation-list,.message-stream { min-height: 0; overflow: auto; }.conversation-list { flex: 1 1 auto; height: auto; }.filter-popover { position: absolute; top: 140px; left: 12px; right: 12px; z-index: 6; padding: 16px; border-radius: 14px; box-shadow: 0 14px 42px rgba(0,0,0,.18); }.filter-popover-header,.filter-actions { display: flex; align-items: center; gap: 8px; }.filter-popover-header { margin-bottom: 12px; }.filter-actions { margin-top: 14px; }
.conversation-item { display: flex; width: 100%; align-items: center; gap: 10px; border: 0; background: transparent; padding: 12px; text-align: left; }.conversation-item:hover,.conversation-item.active { background: #edf8f1; }
.presence-dot { width: 10px; height: 10px; border-radius: 999px; background: #94a3b8; box-shadow: 0 0 0 3px rgba(148,163,184,.16); }.presence-online { background: #22c55e; box-shadow: 0 0 0 3px rgba(34,197,94,.16); }.presence-away { background: #f59e0b; box-shadow: 0 0 0 3px rgba(245,158,11,.16); }.presence-offline { background: #64748b; }
.conversation-copy { min-width: 0; flex: 1; }.conversation-copy strong,.conversation-copy small,.linked-client small,.sale-item small { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.avatar-initials { color: #1f8f55; font-size: 13px; font-weight: 800; letter-spacing: .3px; }
.chat-panel { display: flex; min-height: 0; overflow: hidden; flex-direction: column; background: #efeae2; }.chat-header,.composer { flex: 0 0 auto; background: #f7faf8; }.message-stream { flex: 1 1 auto; padding: 22px; }
.message-row { display: flex; margin-bottom: 8px; }.message-row.outgoing { justify-content: flex-end; }.message-bubble { position: relative; max-width: 72%; padding: 9px 32px 9px 12px; border-radius: 10px; background: #fff; box-shadow: 0 1px 2px rgba(0,0,0,.08); }.outgoing .message-bubble { background: #d9fdd3; }.message-bubble small { display: block; color: #708078; text-align: right; margin-top: 4px; font-size: 10px; }.delete-message { position: absolute; top: 1px; right: 1px; opacity: .45; }
.message-media { display: block; max-width: min(420px, 100%); max-height: 360px; border-radius: 8px; }.message-audio { display: block; width: min(310px, 100%); }.poll-message { min-width: 220px; }.poll-option { display: flex; justify-content: space-between; gap: 16px; padding: 7px 0; border-top: 1px solid rgba(112,128,120,.22); }.poll-option:first-of-type { margin-top: 7px; }.poll-option small { margin: 0; white-space: nowrap; }
.empty-state,.empty-chat { padding: 28px; color: #7b8c84; text-align: center; }.empty-chat { margin: auto; }.linked-clients-list { display: flex; flex-direction: column; gap: 8px; margin-top: 10px; }.linked-clients-list.compact { margin-top: 0; }.linked-client,.sale-item { padding: 12px; border-radius: 10px; background: #f3f8f5; margin-top: 0; }
.client-panel-screen { min-height: 100%; }
.client-action-list { display: flex; flex-direction: column; gap: 10px; }
.client-action-item { display: grid; grid-template-columns: 42px minmax(0, 1fr) 24px; align-items: center; gap: 10px; width: 100%; border: 1px solid #dfe9e4; border-radius: 16px; padding: 12px; background: #f8fbf9; color: #20342a; text-align: left; transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease; }
.client-action-item:hover { transform: translateX(3px); border-color: #bfe6cf; box-shadow: 0 12px 28px rgba(31,143,85,.12); }
.client-action-item strong,.client-action-item small,.appointment-card strong,.appointment-card small,.timeline-item strong,.timeline-item small { display: block; }
.client-action-item small,.appointment-card small,.timeline-item small,.assume-card small,.appointment-review small { color: #6f8178; }
.client-action-icon { width: 42px; height: 42px; border-radius: 14px; display: flex; align-items: center; justify-content: center; background: #e8f7ef; color: #1f8f55; }
.client-panel-nav { position: sticky; top: -18px; z-index: 4; display: flex; align-items: center; gap: 8px; margin: -18px -18px 14px; padding: 14px 18px; background: inherit; border-bottom: 1px solid #e5ece8; }
.client-detail-section { display: flex; flex-direction: column; gap: 12px; }
.assume-card,.appointment-card,.appointment-review { display: flex; flex-direction: column; gap: 8px; padding: 14px; border-radius: 16px; background: #f3f8f5; border: 1px solid #dfe9e4; }
.timeline-list,.appointment-list { display: flex; flex-direction: column; gap: 10px; }
.timeline-item { position: relative; display: grid; grid-template-columns: 16px minmax(0, 1fr); gap: 8px; padding: 10px 0; }
.timeline-dot { width: 10px; height: 10px; margin-top: 5px; border-radius: 999px; background: #1f8f55; box-shadow: 0 0 0 4px rgba(31,143,85,.14); }
.step-pill { width: max-content; padding: 6px 10px; border-radius: 999px; background: #e8f7ef; color: #1f8f55; font-size: 12px; font-weight: 800; }
.appointment-type-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
.appointment-type-card { min-height: 92px; display: flex; flex-direction: column; align-items: flex-start; justify-content: center; gap: 8px; border: 1px solid #dfe9e4; border-radius: 16px; padding: 12px; background: #f8fbf9; color: #20342a; text-align: left; }
.appointment-type-card.active,.appointment-type-card:hover { border-color: #1f8f55; background: #e8f7ef; }
.appointment-form { display: flex; flex-direction: column; gap: 2px; }
.step-actions { display: flex; align-items: center; gap: 8px; margin-top: 4px; }
.panel-slide-enter-active,.panel-slide-leave-active,.panel-slide-back-enter-active,.panel-slide-back-leave-active { transition: all .22s ease; }
.panel-slide-enter { opacity: 0; transform: translateX(34px); }.panel-slide-leave-to { opacity: 0; transform: translateX(-34px); }
.panel-slide-back-enter { opacity: 0; transform: translateX(-34px); }.panel-slide-back-leave-to { opacity: 0; transform: translateX(34px); }
.sales-header { display: flex; align-items: center; gap: 8px; }
.sales-filter-popover { position: absolute; top: 150px; left: 12px; right: 12px; z-index: 7; padding: 16px; border-radius: 14px; box-shadow: 0 14px 42px rgba(0,0,0,.18); }
.sales-history-list { display: flex; flex-direction: column; gap: 8px; }
.sales-history-list ::v-deep .card-list-item { width: 100%; margin-bottom: 0; box-shadow: 0 10px 24px rgba(0,0,0,.14); }
.sales-history-list ::v-deep .expande-horizonal,.sales-history-list ::v-deep .expande-horizontal { width: 100%; }
.internal-chat-snackbar { padding: 0 !important; background: transparent !important; box-shadow: none !important; }
.internal-snackbar-card { display: grid; grid-template-columns: 34px 44px minmax(0, 1fr) 22px; align-items: center; gap: 12px; width: min(440px, calc(100vw - 32px)); padding: 12px 14px; border-radius: 14px; background: #fff; border: 1px solid #dfe9e4; box-shadow: 0 16px 44px rgba(10,28,21,.18); cursor: pointer; transition: transform .18s ease, box-shadow .18s ease; }
.internal-snackbar-card:hover { transform: translateY(-2px); box-shadow: 0 20px 54px rgba(10,28,21,.22); }
.create-whatsapp-client-card { border-radius: 22px !important; overflow: hidden; background: #f7faf8 !important; color: #173b33; }
.create-client-hero { display: flex; align-items: center; gap: 12px; padding: 18px; background: linear-gradient(135deg, #e8f7ef, #ffffff); border-bottom: 1px solid #dfe9e4; }
.create-client-hero strong,.create-client-hero small { display: block; }
.create-client-hero strong { font-size: 17px; font-weight: 800; }
.create-client-hero small { color: #667781; }
.create-whatsapp-client-card.dark { background: #111b21 !important; color: #e9edef; }
.create-whatsapp-client-card.dark .create-client-hero { background: linear-gradient(135deg, #0b3b2a, #182229); border-color: #26343c; }
.create-whatsapp-client-card.dark .create-client-hero small { color: #8ea39a; }
.create-whatsapp-client-card.dark ::v-deep .v-input__slot { background: #182229 !important; color: #e9edef !important; }
.create-whatsapp-client-card.dark ::v-deep label,
.create-whatsapp-client-card.dark ::v-deep input,
.create-whatsapp-client-card.dark ::v-deep textarea { color: #e9edef !important; }
.create-client-actions { align-items: center; gap: 10px; padding: 12px 18px 18px !important; }
.create-client-submit { background: #1f8f55 !important; color: #ffffff !important; caret-color: #ffffff !important; font-weight: 800; letter-spacing: 0; text-transform: none; }
.create-client-submit ::v-deep .v-btn__content { color: #ffffff !important; opacity: 1 !important; }
@media(max-width: 520px) {
  .create-client-actions { align-items: stretch; flex-direction: column-reverse; }
  .create-client-actions .v-btn { margin-left: 0 !important; width: 100%; }
  .create-client-actions .spacer { display: none; }
}
.internal-snackbar-icon { width: 34px; height: 34px; display: flex; align-items: center; justify-content: center; border-radius: 10px; background: #075e54 !important; border: 1px solid #075e54; }
.internal-snackbar-avatar { background: #075e54 !important; border: 1px solid #075e54; }
.internal-snackbar-glyph.v-icon,
.internal-snackbar-icon .v-icon,
.internal-snackbar-avatar .v-icon { color: #ffffff !important; caret-color: #ffffff !important; opacity: 1 !important; filter: none !important; }
.internal-snackbar-glyph.v-icon::before,
.internal-snackbar-icon .v-icon::before,
.internal-snackbar-avatar .v-icon::before { color: #ffffff !important; opacity: 1 !important; }
.internal-snackbar-chevron { color: #075e54 !important; opacity: 1 !important; }
.internal-snackbar-copy { min-width: 0; color: #173b33; }
.internal-snackbar-copy strong,.internal-snackbar-copy small { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.internal-snackbar-copy strong { font-size: 14px; font-weight: 800; line-height: 1.25; }
.internal-snackbar-copy small { margin-top: 3px; color: #667781; }
.internal-snackbar-chevron { justify-self: end; }
.internal-snackbar-action { margin-right: 4px; color: #173b33 !important; background: #eef5f1 !important; border: 1px solid #d5e4dc; }
.internal-snackbar-action-dark { color: #e9edef !important; background: #0b3b2a !important; }
.internal-snackbar-card-dark { background: #111b21; border-color: #26343c; box-shadow: 0 16px 44px rgba(0,0,0,.34); }
.internal-snackbar-card-dark .internal-snackbar-icon,
.internal-snackbar-card-dark .internal-snackbar-avatar { background: #1f8f55 !important; border-color: #1f8f55; }
.internal-snackbar-card-dark .internal-snackbar-icon .v-icon,
.internal-snackbar-card-dark .internal-snackbar-avatar .v-icon,
.internal-snackbar-card-dark .internal-snackbar-icon .v-icon::before,
.internal-snackbar-card-dark .internal-snackbar-avatar .v-icon::before { color: #ffffff !important; opacity: 1 !important; }
.internal-snackbar-card-dark .internal-snackbar-chevron { color: #9be7bd !important; opacity: 1 !important; }
.internal-snackbar-card-dark .internal-snackbar-copy { color: #eef4f1; }
.internal-snackbar-card-dark .internal-snackbar-copy small { color: #9fb0a8; }
.whatsapp-workspace-dark .conversation-sidebar,.whatsapp-workspace-dark .client-panel { background: #111b21; border-color: #243038; }.whatsapp-workspace-dark .chat-panel { background: #061014; }.whatsapp-workspace-dark .chat-header,.whatsapp-workspace-dark .composer { background: #182229; }.whatsapp-workspace-dark .conversation-tabs ::v-deep .v-tabs-bar { background: #111b21; }.whatsapp-workspace-dark .filter-popover,.whatsapp-workspace-dark .sales-filter-popover { background: #182229; border: 1px solid #26343c; }.whatsapp-workspace-dark .conversation-item { color: #eef4f1; }.whatsapp-workspace-dark .conversation-copy small,.whatsapp-workspace-dark .chat-header small { color: #8ea39a; }.whatsapp-workspace-dark .conversation-item:hover,.whatsapp-workspace-dark .conversation-item.active { background: #202c33; }.whatsapp-workspace-dark .message-bubble { background: #182229; color: #eef4f1; }.whatsapp-workspace-dark .outgoing .message-bubble { background: #00483d; }.whatsapp-workspace-dark .linked-client,.whatsapp-workspace-dark .sale-item,.whatsapp-workspace-dark .assume-card,.whatsapp-workspace-dark .appointment-card,.whatsapp-workspace-dark .appointment-review { background: #182229; border-color: #26343c; }.whatsapp-workspace-dark .avatar-initials { color: #9be7bd; }
.whatsapp-workspace-dark .client-action-item,.whatsapp-workspace-dark .appointment-type-card { background: #182229; border-color: #26343c; color: #eef4f1; }.whatsapp-workspace-dark .client-action-item:hover,.whatsapp-workspace-dark .appointment-type-card.active,.whatsapp-workspace-dark .appointment-type-card:hover { background: #202c33; border-color: #1f8f55; }.whatsapp-workspace-dark .client-action-icon,.whatsapp-workspace-dark .step-pill { background: #0b3b2a; color: #9be7bd; }.whatsapp-workspace-dark .client-panel-nav { border-color: #26343c; }.whatsapp-workspace-dark .client-action-item small,.whatsapp-workspace-dark .appointment-card small,.whatsapp-workspace-dark .timeline-item small,.whatsapp-workspace-dark .assume-card small,.whatsapp-workspace-dark .appointment-review small { color: #8ea39a; }
.whatsapp-workspace-dark .conversation-list,.whatsapp-workspace-dark .message-stream,.whatsapp-workspace-dark .client-panel { scrollbar-color: #40515a #111b21; }
.whatsapp-workspace-dark .conversation-list::-webkit-scrollbar,.whatsapp-workspace-dark .message-stream::-webkit-scrollbar,.whatsapp-workspace-dark .client-panel::-webkit-scrollbar { width: 10px; height: 10px; }
.whatsapp-workspace-dark .conversation-list::-webkit-scrollbar-track,.whatsapp-workspace-dark .message-stream::-webkit-scrollbar-track,.whatsapp-workspace-dark .client-panel::-webkit-scrollbar-track { background: #111b21; }
.whatsapp-workspace-dark .conversation-list::-webkit-scrollbar-thumb,.whatsapp-workspace-dark .message-stream::-webkit-scrollbar-thumb,.whatsapp-workspace-dark .client-panel::-webkit-scrollbar-thumb { background: #40515a; border: 2px solid #111b21; border-radius: 999px; }
@media(max-width: 1100px) { .whatsapp-workspace { grid-template-columns: 250px minmax(320px, 1fr) 250px; } }
</style>
